import { useContext, useState } from 'react';
import { ToastNotificationContext } from '../../../../../../../common/toast-notification';
import { BusinessError } from '../../../../../../../common/business-error';
import { addParams, fetchWithTokenRefresh } from '../../../../../../../../api/fetchWithTokenRefresh';
import endpoints from '../../../../../../../../api/endpoints';
import { handleResponseError } from '../../../../../../../../api/handleResponseError';
import { mapToApiFormat } from '../../dataTransformator';
import { UpdateUserRole } from '../../interfaces';
import { getUserRoleUpdateBusinessError } from '../businessErrors';
import { useDialog } from '../../../../../../../common/dialog/useDialog';

type DialogError = BusinessError | null;

type ApiResponse = {
  message: string;
  status: string;
};

interface Props {
  handleSuccess: () => void;
}

export const useUserRoleUpdate = ({ handleSuccess }: Props) => {
  const { openToast } = useContext(ToastNotificationContext);
  const { isDialogOpen, handleOpenDialog, handleCloseDialog } = useDialog();
  const [updateLoading, setLoading] = useState<boolean>(false);
  const [updateError, setError] = useState<DialogError>(null);

  const handleApiResponse = (response: ApiResponse) => {
    switch (response.status) {
      case 'OK':
        openToast({
          message: 'user was successfully updated',
          severity: 'success'
        });
        break;
      case 'UNKNOWN_ROLE':
        openToast({
          message: 'unknown role provided',
          severity: 'error'
        });
        setError({ message: 'unknown role provided', severity: 'error' });
        break;
      case 'UNKNOWN_USER':
        openToast({
          message: 'unknown user provided',
          severity: 'error'
        });
        setError({ message: 'unknown user provided', severity: 'error' });
        break;
      default:
        openToast({
          message: 'general error',
          severity: 'error'
        });
        setError({ message: 'unknown error', severity: 'error' });
        break;
    }
  };

  const handleUserRoleUpdate = async (selectedUser: UpdateUserRole) => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetchWithTokenRefresh(
        endpoints.accountsManagementActive,
        addParams('PUT', mapToApiFormat({ userId: selectedUser.userId, roleId: selectedUser.roleId }))
      );
      const jsonResponse = await response.json();

      if (response.status !== 200) {
        await handleResponseError(response);
      }
      const parsed: ApiResponse = jsonResponse;
      handleApiResponse(parsed);

      setLoading(false);
      handleCloseDialog();
      handleSuccess();
    } catch (e) {
      const { message, severity } = getUserRoleUpdateBusinessError();

      setError({ message, severity });
      setLoading(false);
    }
  };

  return {
    userUpdateModalOpen: isDialogOpen,
    handleCloseUserRoleUpdate: handleCloseDialog,
    handleOpenUserRoleUpdate: handleOpenDialog,
    handleUserRoleUpdate,
    updateLoading,
    updateError
  };
};
